import { isEmpty } from 'lodash';
import { Platform } from 'react-native';

import { getFileExtension, getFileExtensionFromUri } from '@/utils/getFileExtension';

import { client } from '..';
import {
  AcceptTermsResponse,
  AddAchResponse,
  AddCreditCardResponse,
  CreateNpoPaymentAccountStepsParams,
  GetTermsResponse,
  NpoPaymentAccountStepsResponse,
  NpoPaymentAccountStepsResult,
  PaymentMethodListResponse,
  SubscriptionsListResponse,
  TransactionsListResponse,
  ValidateAchResponse,
  GetTransactionHistoryPdfParams,
  GetTransactionHistoryCsvParams
} from '../models/paymentAccountsModels';

// NPO APIs
export const getTermsAndConditionsCreateAccountNpo = async ({
  nonProfitOrgId,
  type
}: {
  nonProfitOrgId: string;
  type?: string;
}): Promise<GetTermsResponse> => {
  const typeParam = type !== undefined ? `?type=${type}` : ``;

  const { data } = await client.post(`/payment-accounts/terms-and-conditions${typeParam}`, {
    nonProfitOrgId
  });

  return data;
};

export const acceptTermsAndConditionsNpo = async ({
  correlationId,
  termsAndConditionsId,
  nonProfitOrgId
}: {
  correlationId: string;
  termsAndConditionsId: string;
  nonProfitOrgId: string;
}): Promise<AcceptTermsResponse> => {
  const { data } = await client.patch('/payment-accounts/terms-and-conditions/accept', {
    correlationId,
    termsAndConditionsId,
    nonProfitOrgId
  });
  return data;
};

export const getNpoPaymentAccountSteps = async ({
  npoId
}: {
  npoId: string;
}): Promise<NpoPaymentAccountStepsResponse> => {
  const { data } = await client.post(`/payment-accounts/get-save-steps-account`, { npoId });

  return data;
};

export const createNpoPaymentAccountSteps = async ({
  type,
  body
}: {
  type: string;
  body: CreateNpoPaymentAccountStepsParams;
}): Promise<NpoPaymentAccountStepsResult> => {
  const typeParam = type !== undefined ? `?type=${type}` : ``;

  const { data } = await client.post(`/payment-accounts/save-steps-account${typeParam}`, body);

  return data;
};

export const saveStepsNpoPaymentAccount = async (body: { nonProfitOrgId: string }): Promise<any> => {
  const { data } = await client.post(`/payment-accounts/create-account/steps`, body);

  return data;
};

export const createNpoPaymentAccountDocuments = async ({
  body,
  voidedCheck,
  driverLicense,
  doc501c3,
  articlesOfIncorporation,
  bankStatements
}: {
  body: { nonProfitOrgId: string };
  voidedCheck: string | File;
  driverLicense: string | File;
  doc501c3: string | File;
  articlesOfIncorporation?: string | File | undefined;
  bankStatements?: string | File | undefined;
}): Promise<any> => {
  const formData = new FormData();
  if (Platform.OS === 'web') {
    const voidedCheckExtension = getFileExtension(voidedCheck as File);
    formData.append(
      'voidedCheckedOrSignedBankLetter',
      voidedCheck as File,
      `voidedCheckedOrSignedBankLetter_${body.nonProfitOrgId}.${!isEmpty(voidedCheckExtension) ? voidedCheckExtension : 'pdf'}`
    );

    const driverLicenseExtension = getFileExtension(driverLicense as File);
    formData.append(
      'driversLicense',
      driverLicense as File,
      `driversLicense_${body.nonProfitOrgId}.${!isEmpty(driverLicenseExtension) ? driverLicenseExtension : 'pdf'}`
    );

    const doc501c3Extension = getFileExtension(doc501c3 as File);
    formData.append(
      '501c3',
      doc501c3 as File,
      `501c3_${body.nonProfitOrgId}.${!isEmpty(doc501c3Extension) ? doc501c3Extension : 'pdf'}`
    );
    if (articlesOfIncorporation) {
      const articlesOfIncorporationExtension = getFileExtension(articlesOfIncorporation as File);
      formData.append(
        'articleOfIncorporation',
        articlesOfIncorporation as File,
        `articleOfIncorporation_${body.nonProfitOrgId}.${!isEmpty(articlesOfIncorporationExtension) ? articlesOfIncorporationExtension : 'pdf'}`
      );
    }
    if (bankStatements) {
      const bankStatementsExtension = getFileExtension(bankStatements as File);
      formData.append(
        '3monthsOfBankStatements',
        bankStatements as File,
        `3monthsOfBankStatements_${body.nonProfitOrgId}.${!isEmpty(bankStatementsExtension) ? bankStatementsExtension : 'pdf'}`
      );
    }
  } else {
    const voidedCheckExtension = getFileExtensionFromUri(voidedCheck as string);
    formData.append('voidedCheckedOrSignedBankLetter', {
      uri: voidedCheck,
      type: 'application/pdf',
      name: `voided_check_${body.nonProfitOrgId}.${!isEmpty(voidedCheckExtension) ? voidedCheckExtension : 'pdf'}`
    } as any);

    const driverLicenseExtension = getFileExtensionFromUri(driverLicense as string);
    formData.append('driversLicense', {
      uri: driverLicense,
      type: 'application/pdf',
      name: `driver_license_${body.nonProfitOrgId}.${!isEmpty(driverLicenseExtension) ? driverLicenseExtension : 'pdf'}`
    } as any);

    const doc501c3Extension = getFileExtensionFromUri(doc501c3 as string);
    formData.append('501c3', {
      uri: doc501c3,
      type: 'application/pdf',
      name: `501c3_${body.nonProfitOrgId}.${!isEmpty(doc501c3Extension) ? doc501c3Extension : 'pdf'}`
    } as any);
    if (articlesOfIncorporation) {
      const articlesOfIncorporationExtension = getFileExtensionFromUri(articlesOfIncorporation as string);
      formData.append('articleOfIncorporation', {
        uri: articlesOfIncorporation,
        type: 'application/pdf',
        name: `articleOfIncorporation_${body.nonProfitOrgId}.${!isEmpty(articlesOfIncorporationExtension) ? articlesOfIncorporationExtension : 'pdf'}`
      } as any);
    }
    if (bankStatements) {
      const bankStatementsExtension = getFileExtensionFromUri(bankStatements as string);
      formData.append('3monthsOfBankStatements', {
        uri: bankStatements,
        type: 'application/pdf',
        name: `3monthsOfBankStatements_${body.nonProfitOrgId}.${!isEmpty(bankStatementsExtension) ? bankStatementsExtension : 'pdf'}`
      } as any);
    }
  }

  formData.append('nonProfitOrgId', body.nonProfitOrgId);

  const { data } = await client.post(`/payment-accounts/upload-files-account`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return data;
};

// Individual APIs
export const getListPaymentMethods = async (body: { npoId: string }): Promise<PaymentMethodListResponse> => {
  const { data } = await client.post('/payment-accounts/list-payments-methods', body);

  return data;
};

export const deleteIndividualCustomer = async (body: { customerKey: string; npoId: string }): Promise<any> => {
  const { data } = await client.delete(`/payment-accounts/delete-customer`, { data: body });

  return data;
};

// Credit Card
export const individualAddCreditCard = async (body: {
  npoId: string | null;
  card_number: string;
  expiration_date: string;
  name_on_card: string;
  street1: string;
  zip_code: string;
  cvv: string;
}): Promise<AddCreditCardResponse> => {
  const { data } = await client.post(`/payment-accounts/credit-card`, body);

  return data;
};

export const partialEditCreditCardIndividual = async ({ cardKey }: { cardKey: string }): Promise<any> => {
  const { data } = await client.patch(`/payment-accounts/partial-edit-credit-card/${cardKey}`);

  return data;
};

export const getCreditCardInfoIndividual = async (body: { npoId: string }): Promise<any> => {
  const { data } = await client.post('/payment-accounts/get-credit-card', body);

  return data;
};

export const deleteCreditCardIndividual = async (cardKey: string): Promise<any> => {
  const { data } = await client.delete(`/payment-accounts/credit-card/${cardKey}`);

  return data;
};

//ACH Check
export const individualAddAchCheck = async (body: {
  check_type: 'PERSONAL' | 'BUSINESS';
  account_type: 'CHECKING' | 'SAVINGS';
  name_on_check: string;
  account_number: string;
  transit_number: string;
}): Promise<AddAchResponse> => {
  const { data } = await client.post(`/payment-accounts/ach-check`, body);

  return data;
};

export const partialEditAchCheckIndividual = async (checkKey: string): Promise<any> => {
  const { data } = await client.patch(`/payment-accounts/ach-check/${checkKey}`);

  return data;
};

export const deleteAchCheckIndividual = async (checkKey: string): Promise<any> => {
  const { data } = await client.delete(`/payment-accounts/ach-check/${checkKey}`);

  return data;
};

export const validateAchCheck = async ({ routingNumber }: { routingNumber: string }): Promise<ValidateAchResponse> => {
  const { data } = await client.get(`/payment-accounts/abalookup/${routingNumber}`);

  return data;
};

// Subscriptions
export const getSubscriptionsListScrolling = async ({
  queryKey,
  pageParam
}: {
  queryKey: (string | number | undefined)[];
  pageParam: string;
}): Promise<SubscriptionsListResponse> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, n, limit] = queryKey;

  const url = `/donations/subscriptions?n=${pageParam}&limit=${limit}`;

  const { data } = await client.get(url);

  return data;
};

export const editSubscription = async ({
  idSub,
  bill_amount,
  due_date,
  billing_period,
  payment_method,
  key
}: {
  idSub: string;
  bill_amount: string;
  due_date: string;
  billing_period: string;
  payment_method?: string;
  key?: string;
}): Promise<any> => {
  const { data } = await client.put(`/donations/subscriptions/${idSub}`, {
    bill_amount,
    due_date,
    payment_method,
    billing_period,
    key
  });

  return data;
};

export const deleteSubscription = async (idSub: string): Promise<any> => {
  const { data } = await client.delete(`/donations/subscriptions/${idSub}`);

  return data;
};

// Transactions
export const getTransactionsListScrolling = async ({
  queryKey,
  pageParam
}: {
  queryKey: (string | number | undefined)[];
  pageParam: string | undefined;
}): Promise<TransactionsListResponse> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, __, limit] = queryKey;

  const url = `/donations/transaction-history?limit=${limit}${pageParam !== undefined ? `&n=${pageParam}` : ''}`;

  const { data } = await client.get(url);

  return data;
};

export const refundTransactionNPO = async (idTransaction: string): Promise<any> => {
  const { data } = await client.post(`/donations/transactions/${idTransaction}/refund`);

  return data;
};

export const deleteTransactionIndividual = async (idTransaction: string): Promise<any> => {
  const { data } = await client.post(`/donations/transactions/${idTransaction}/void`);

  return data;
};

export const getTransactionHistoryPdf = async (params: GetTransactionHistoryPdfParams): Promise<any> => {
  const { data } = await client.get('/donations/transaction-history/pdf', {
    params,
    responseType: 'blob'
  });

  return data;
};

export const getTransactionHistoryCsv = async (params: GetTransactionHistoryCsvParams): Promise<any> => {
  const { data } = await client.get('/donations/transaction-history/csv', {
    params
  });

  return data;
};
